import { useAuth0 } from "@auth0/auth0-react";
import { Link, useLocation } from "react-router-dom";
import { useCreditsContext } from "../context/CreditsContext";

// Navigation items configuration
const NAV_ITEMS = [
  { path: "/app", label: "Generate" },
  { path: "/saved", label: "History" },
  { path: "/favorites", label: "Favorites" },
] as const;

// Sub-component: Navigation Link with active state detection
interface NavLinkProps {
  to: string;
  label: string;
  isActive: boolean;
}

function NavLink({ to, label, isActive }: NavLinkProps) {
  return (
    <Link to={to}>
      <button
        className={`
          rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200
          ${
            isActive
              ? "bg-indigo-100 text-indigo-700 font-semibold shadow-sm"
              : "text-gray-600 hover:text-indigo-600 hover:bg-indigo-50"
          }
        `}
      >
        {label}
      </button>
    </Link>
  );
}

// Sub-component: Credits Badge
interface CreditsBadgeProps {
  credits: number;
  hasUnlimitedCredits: boolean;
  userTier: string;
}

function CreditsBadge({ credits, hasUnlimitedCredits, userTier }: CreditsBadgeProps) {
  return (
    <div className="flex items-center gap-2 bg-indigo-50 px-3 py-1.5 rounded-lg border border-indigo-100 shadow-sm">
      <span className="text-sm font-medium">
        {hasUnlimitedCredits ? (
          <span className="text-indigo-700 font-semibold">∞ Pro</span>
        ) : (
          <>
            <span className={credits === 0 ? "text-red-600 font-semibold" : "text-indigo-700 font-semibold"}>
              {credits}
            </span>
            <span className="text-gray-500 ml-1 font-normal">
              / {userTier === "anonymous" ? "3" : "10"}
            </span>
          </>
        )}
      </span>
    </div>
  );
}

// Sub-component: User Menu
interface UserMenuProps {
  user: any;
  onLogout: () => void;
}

function UserMenu({ user, onLogout }: UserMenuProps) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        {user?.picture && (
          <img
            src={user.picture}
            alt={user.name || "User"}
            className="w-8 h-8 rounded-full border-2 border-indigo-200 shadow-sm"
          />
        )}
        <span className="text-sm text-gray-700 font-medium hidden sm:inline">
          {user?.name || user?.email}
        </span>
      </div>
      <button
        className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-200"
        onClick={onLogout}
      >
        Log Out
      </button>
    </div>
  );
}

// Main TopBar component
export function TopBar() {
  const location = useLocation();
  const { loginWithRedirect, logout, isAuthenticated, user, isLoading } = useAuth0();
  const { credits, hasUnlimitedCredits, userTier } = useCreditsContext();

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleLogin = () => {
    loginWithRedirect({
      appState: { returnTo: "/app" },
    });
  };

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Left section: Logo and Navigation */}
        <div className="flex items-center">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="text-xl font-bold text-gray-900 flex items-center gap-2 hover:text-indigo-600 transition-colors duration-200">
              Blitzname
            </Link>
          </div>

          {/* Navigation Links */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  label={item.label}
                  isActive={location.pathname === item.path}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right section: Credits Badge and Auth UI */}
        <div className="flex items-center gap-4">
          {/* Credits Badge - Show for all users */}
          {!isLoading && (
            <CreditsBadge
              credits={credits}
              hasUnlimitedCredits={hasUnlimitedCredits}
              userTier={userTier}
            />
          )}

          {/* Auth UI */}
          {isLoading ? (
            <div className="w-8 h-8 animate-pulse bg-indigo-100 rounded-full"></div>
          ) : !isAuthenticated ? (
            <button
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-all duration-200 shadow-sm hover:shadow-md"
              onClick={handleLogin}
            >
              Log In
            </button>
          ) : (
            <UserMenu user={user} onLogout={handleLogout} />
          )}
        </div>
      </div>
    </nav>
  );
}
